// @ts-nocheck
/**
 * 借金状態表示・返済画面
 * 借金の詳細と返済方法を表示
 *
 * サーバーファーストアーキテクチャ:
 * - 残り時間はサーバーが計算した display_text を使用（タイムスタンプベース）
 * - 返済操作はサーバーに先に送信し、成功したらローカル更新
 */

import { economyAccount } from '../../../lib/economyClient';
import React, { useState, useMemo } from 'react';
import {
  hasDebt,
  getDebtSummary,
  validateRepayment,
  repayDebt,
  getSeizurePreview,
  DEBT_CONSTANTS,
} from '../systems/debtManager';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { saveInvestmentDebtToServer, DebtWithTimeInfo } from '../../../lib/supabase';

interface DebtStatusProps {
  playerData: {
    gold: number;
    investmentData?: {
      debt?: DebtWithTimeInfo | null;
    };
  };
  updatePlayerData: (fn: (prev: any) => any) => void;
  currentBattleNumber?: number;  // レガシー互換用（タイムスタンプ版では未使用）
  serverDebt?: DebtWithTimeInfo | null;  // サーバーから取得した借金データ
  onClose: () => void;
}

const DebtStatus = ({
  playerData,
  updatePlayerData,
  currentBattleNumber = 0,
  serverDebt,
  onClose,
}: DebtStatusProps) => {
  const isMobile = useIsMobile();
  const [repayAmount, setRepayAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const investmentData = playerData.investmentData || {};
  // サーバーデータを優先、なければローカルデータを使用
  const storedDebt = investmentData.debt;
  const debtData = economyAccount() && storedDebt ? { ...storedDebt,
    remaining_days: Math.max(0, (storedDebt.deadline - Date.now()) / 86400000),
    remaining_hours: Math.max(0, (storedDebt.deadline - Date.now()) / 3600000),
    is_overdue: storedDebt.deadline <= Date.now(),
    display_text: storedDebt.deadline <= Date.now() ? '期限切れ' : `あと${Math.ceil((storedDebt.deadline - Date.now()) / 3600000)}時間`,
  } : storedDebt;
  const gold = playerData.gold;

  // サーバーデータがあればそれを使用、なければ従来のclient計算
  const summary = useMemo(() => {
    if (!debtData || !hasDebt(debtData)) {
      return {
        hasDebt: false,
        amount: 0,
        originalAmount: 0,
        interest: 0,
        remainingDays: 0,
        isOverdue: false,
        nextInterest: 0,
        displayText: '',
      };
    }

    // サーバーから残り時間情報が提供されている場合はそれを使用
    const hasServerTimeInfo = 'display_text' in debtData && debtData.display_text;

    if (hasServerTimeInfo) {
      return {
        hasDebt: true,
        amount: debtData.amount + (debtData.interest || 0),
        originalAmount: debtData.originalAmount,
        interest: debtData.interest || 0,
        remainingDays: debtData.remaining_days || 0,
        remainingHours: debtData.remaining_hours || 0,
        isOverdue: debtData.is_overdue || false,
        nextInterest: Math.round(debtData.amount * DEBT_CONSTANTS.INTEREST_RATE),
        displayText: debtData.display_text,
        deadline: debtData.deadline,
        createdAt: debtData.createdAt,
        source: debtData.source,
      };
    }

    // フォールバック: 従来のクライアント計算（後方互換性）
    return getDebtSummary(debtData, currentBattleNumber);
  }, [debtData, currentBattleNumber]);

  // 差し押さえ予定リスト（簡易版）
  const seizurePreview = useMemo(() => {
    if (!hasDebt(debtData)) return [];
    // 簡易的にコレクションから高い順に取得
    // 実際の実装ではvalueCalculatorを使用
    return [];
  }, [debtData]);

  // 返済処理（サーバー優先）
  const handleRepay = async () => {
    const amount = parseInt(repayAmount, 10);
    if (isNaN(amount) || amount <= 0 || isLoading) return;

    const validation = validateRepayment(debtData, gold, amount);
    if (!validation.valid) {
      setErrorMessage(validation.error);
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const { newDebtData, actualRepayment } = repayDebt(debtData, amount);

      // サーバー保存を先に実行
      const serverResult = await saveInvestmentDebtToServer({
        action: 'repay',
        amount: economyAccount() ? amount : actualRepayment,
      });

      // 未認証の場合はローカルのみで処理
      if (serverResult.error === 'Not authenticated') {
        console.log('[DebtStatus] Not authenticated, proceeding with local only');
      } else if (!serverResult.success) {
        // サーバーエラーの場合は処理を中止
        setErrorMessage(`サーバー保存に失敗しました: ${serverResult.error}`);
        return;
      } else {
        setRepayAmount(''); return;
      }

      // ローカル処理
      updatePlayerData((prev) => ({
        ...prev,
        gold: prev.gold - actualRepayment,
        investmentData: {
          ...prev.investmentData,
          debt: newDebtData,
        },
      }));

      setRepayAmount('');
    } finally {
      setIsLoading(false);
    }
  };

  // 全額返済（サーバー優先）
  const handleRepayAll = async () => {
    if (!hasDebt(debtData) || isLoading) return;

    const amount = Math.min(debtData.amount + (debtData.interest || 0), gold);
    if (amount <= 0) {
      setErrorMessage('所持金が不足しています');
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const { newDebtData, actualRepayment } = repayDebt(debtData, amount);

      // サーバー保存を先に実行
      const serverResult = await saveInvestmentDebtToServer({
        action: 'repay',
        amount: economyAccount() ? amount : actualRepayment,
      });

      // 未認証の場合はローカルのみで処理
      if (serverResult.error === 'Not authenticated') {
        console.log('[DebtStatus] Not authenticated, proceeding with local only');
      } else if (!serverResult.success) {
        // サーバーエラーの場合は処理を中止
        setErrorMessage(`サーバー保存に失敗しました: ${serverResult.error}`);
        return;
      } else {
        return;
      }

      // ローカル処理
      updatePlayerData((prev) => ({
        ...prev,
        gold: prev.gold - actualRepayment,
        investmentData: {
          ...prev.investmentData,
          debt: newDebtData,
        },
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // スタイル
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      backgroundColor: '#1a1a2e',
      color: '#e0e0e0',
    },
    header: {
      padding: isMobile ? '12px' : '16px',
      borderBottom: '1px solid #333',
      backgroundColor: '#2c0a0a',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    title: {
      fontSize: isMobile ? '18px' : '20px',
      fontWeight: 'bold',
      color: '#ff6b6b',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    closeButton: {
      padding: '8px 16px',
      backgroundColor: '#333',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
    },
    content: {
      flex: 1,
      overflow: 'auto',
      padding: isMobile ? '12px' : '16px',
    },
    debtCard: {
      backgroundColor: 'rgba(255, 107, 107, 0.1)',
      border: '2px solid #ff6b6b',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px',
    },
    debtAmount: {
      fontSize: isMobile ? '28px' : '36px',
      fontWeight: 'bold',
      color: '#ff6b6b',
      textAlign: 'center',
      marginBottom: '16px',
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '12px',
      marginBottom: '16px',
    },
    infoItem: {
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      padding: '12px',
      borderRadius: '4px',
      textAlign: 'center',
    },
    infoLabel: {
      fontSize: '12px',
      color: '#888',
      marginBottom: '4px',
    },
    infoValue: {
      fontSize: '16px',
      fontWeight: 'bold',
    },
    warningBox: {
      backgroundColor: 'rgba(255, 152, 0, 0.1)',
      border: '1px solid #ff9800',
      borderRadius: '8px',
      padding: '12px',
      marginBottom: '16px',
    },
    warningTitle: {
      color: '#ff9800',
      fontWeight: 'bold',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    warningText: {
      fontSize: '14px',
      color: '#aaa',
    },
    repaySection: {
      backgroundColor: 'rgba(76, 175, 80, 0.1)',
      border: '1px solid #4caf50',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px',
    },
    sectionTitle: {
      fontWeight: 'bold',
      marginBottom: '12px',
      color: '#4caf50',
    },
    repayRow: {
      display: 'flex',
      gap: '8px',
      marginBottom: '12px',
      flexWrap: 'wrap',
    },
    input: {
      flex: 1,
      minWidth: '100px',
      padding: '10px',
      border: '1px solid #444',
      borderRadius: '4px',
      backgroundColor: '#1a1a2e',
      color: '#fff',
      fontSize: '16px',
    },
    repayButton: {
      padding: '10px 20px',
      backgroundColor: '#4caf50',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 'bold',
    },
    fullRepayButton: {
      width: '100%',
      padding: '12px',
      backgroundColor: '#2196f3',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 'bold',
    },
    disabledButton: {
      backgroundColor: '#444',
      cursor: 'not-allowed',
    },
    restrictionBox: {
      backgroundColor: 'rgba(244, 67, 54, 0.1)',
      border: '1px solid #f44336',
      borderRadius: '8px',
      padding: '12px',
    },
    restrictionTitle: {
      color: '#f44336',
      fontWeight: 'bold',
      marginBottom: '8px',
    },
    restrictionList: {
      fontSize: '14px',
      color: '#aaa',
      paddingLeft: '16px',
    },
    noDebtBox: {
      textAlign: 'center',
      padding: '40px',
      color: '#4caf50',
    },
    noDebtIcon: {
      fontSize: '48px',
      marginBottom: '16px',
    },
    noDebtText: {
      fontSize: '18px',
    },
    goldInfo: {
      backgroundColor: 'rgba(255, 215, 0, 0.1)',
      border: '1px solid #ffd700',
      borderRadius: '8px',
      padding: '12px',
      marginBottom: '16px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    goldLabel: {
      color: '#ffd700',
    },
    goldValue: {
      fontSize: '20px',
      fontWeight: 'bold',
      color: '#ffd700',
    },
  };

  // 借金がない場合
  if (!summary.hasDebt) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.title}>
            <span>💰</span>
            <span>借金状態</span>
          </div>
          <button style={styles.closeButton} onClick={onClose}>
            閉じる
          </button>
        </div>
        <div style={styles.content}>
          <div style={styles.noDebtBox}>
            <div style={styles.noDebtIcon}>✨</div>
            <div style={styles.noDebtText}>借金はありません</div>
          </div>
        </div>
      </div>
    );
  }

  const canFullRepay = gold >= summary.amount;
  const remainingDays = summary.remainingDays;
  const isOverdue = summary.isOverdue;

  return (
    <div style={styles.container}>
      {/* ヘッダー */}
      <div style={styles.header}>
        <div style={styles.title}>
          <span>💸</span>
          <span>借金状態</span>
        </div>
        <button style={styles.closeButton} onClick={onClose}>
          閉じる
        </button>
      </div>

      <div style={styles.content}>
        {/* 借金カード */}
        <div style={styles.debtCard}>
          <div style={styles.debtAmount}>
            {summary.amount.toLocaleString()}G
          </div>

          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>元本</div>
              <div style={styles.infoValue}>
                {summary.originalAmount.toLocaleString()}G
              </div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>累計利息</div>
              <div style={styles.infoValue}>
                +{summary.interest.toLocaleString()}G
              </div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>返済期限</div>
              <div style={{
                ...styles.infoValue,
                color: isOverdue ? '#f44336' : remainingDays <= 2 ? '#ff9800' : '#fff',
              }}>
                {/* サーバー提供の表示テキストを優先（時間単位まで表示可能） */}
                {summary.displayText || (isOverdue ? '期限切れ' : `あと${remainingDays}日`)}
              </div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>次回利息</div>
              <div style={{ ...styles.infoValue, color: '#ff9800' }}>
                +{summary.nextInterest.toLocaleString()}G/日
              </div>
            </div>
          </div>
        </div>

        {/* 期限切れ警告 */}
        {isOverdue && (
          <div style={styles.warningBox}>
            <div style={styles.warningTitle}>
              <span>⚠️</span>
              <span>返済期限を過ぎました！</span>
            </div>
            <div style={styles.warningText}>
              カードが差し押さえられます。高レアリティのカードから順に没収されます。
            </div>
          </div>
        )}

        {/* 期限が近い警告 */}
        {!isOverdue && remainingDays <= 2 && (
          <div style={styles.warningBox}>
            <div style={styles.warningTitle}>
              <span>⚠️</span>
              <span>返済期限が迫っています</span>
            </div>
            <div style={styles.warningText}>
              {summary.displayText || `あと${remainingDays}日`}以内に返済しないと、カードが差し押さえられます。
            </div>
          </div>
        )}

        {/* 所持金表示 */}
        <div style={styles.goldInfo}>
          <span style={styles.goldLabel}>💰 所持金</span>
          <span style={styles.goldValue}>{gold.toLocaleString()}G</span>
        </div>

        {/* 返済セクション */}
        <div style={styles.repaySection}>
          <div style={styles.sectionTitle}>返済</div>

          {/* エラーメッセージ */}
          {errorMessage && (
            <div style={{ color: '#f44336', marginBottom: '12px', fontSize: '14px' }}>
              ⚠️ {errorMessage}
            </div>
          )}

          <div style={styles.repayRow}>
            <input
              type="number"
              style={styles.input}
              placeholder="返済額を入力"
              value={repayAmount}
              onChange={(e) => setRepayAmount(e.target.value)}
              min="1"
              max={gold}
              disabled={isLoading}
            />
            <button
              style={{
                ...styles.repayButton,
                ...(isLoading || !repayAmount || parseInt(repayAmount, 10) <= 0 ? styles.disabledButton : {}),
              }}
              onClick={handleRepay}
              disabled={isLoading || !repayAmount || parseInt(repayAmount, 10) <= 0}
            >
              {isLoading ? '処理中...' : '返済'}
            </button>
          </div>

          <button
            style={{
              ...styles.fullRepayButton,
              ...(isLoading || gold <= 0 ? styles.disabledButton : {}),
            }}
            onClick={handleRepayAll}
            disabled={isLoading || gold <= 0}
          >
            {isLoading ? '処理中...' : (canFullRepay
              ? `全額返済（${summary.amount.toLocaleString()}G）`
              : `可能な限り返済（${Math.min(summary.amount, gold).toLocaleString()}G）`
            )}
          </button>
        </div>

        {/* 制限事項 */}
        <div style={styles.restrictionBox}>
          <div style={styles.restrictionTitle}>借金中の制限</div>
          <ul style={styles.restrictionList}>
            <li>新規投資・FX取引ができません</li>
            <li>パック購入ができません</li>
            <li>大会への賭けができません</li>
            <li>カード購入ができません（売却は可能）</li>
            <li>対戦報酬の50%が自動返済に充当されます</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DebtStatus;
